import { Button } from "@heroui/react"
import { MoveRight } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useRef, useEffect, useState, useCallback } from 'react';
import { annotate } from 'rough-notation';
import { useMediaQuery } from "@custom-react-hooks/use-media-query";
import { BotIcon } from "../AskMebuki/BotIcon";

// spotlightMode:
//   "none"        — full dim, no hole, only the popover is interactive
//   "spotlight"   — hole around anchor, anchor is visual only (blocked)
//   "interactive" — hole around anchor, anchor is fully interactive


export const TutorialPopover = ({
    className = "",
    startContent = <BotIcon className="size-7 text-base-text shrink-0" outlineColor="#3E3D3C" fill bodyColor="#FFFFFF00" leavesColor="#FFFFFF00" />,
    description = <></>,
    onNext = () => { },
    anchorRef,
    placement = "bottom",

    top = 0, right = 0, left = 0, bottom = 0,
    useRoughUnderline = false,
    hideArrow = false,
    disableScroll = false,
    delay = 600,

    currentStep = null,
    totalSteps = null,

    spotlightPadding = 8,
    spotlightMode = "spotlight",
    containerRef = null,

    dismissable = false,

    type = "onboardingPopover", // nudge | hint
    buttonContent = null,
    hideProgress = false,
    allowSkip = false,

    requiresInput = false,

    hideButtons = false
}) => {
    const [inputValue, setInputValue] = useState("");
    const resolvedDescription = typeof description === 'function'
        ? description({ value: inputValue, onChange: setInputValue })
        : description;

    const isSendDisabled = requiresInput && (!inputValue.trim() || inputValue.length >= 150);

    const isDesktop = useMediaQuery(process.env.REACT_APP_IS_DESKTOP);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const [anchorRect, setAnchorRect] = useState(null);
    const [visible, setVisible] = useState(false);
    const [progressVisible, setProgressVisible] = useState(false);
    const annotationRef = useRef(null);

    const [isPending, setIsPending] = useState(false)
    const [isSkipPending, setIsSkipPending] = useState(false)

    // No anchor to attach to (or explicit placement="center") -> render dead-center, no spotlight
    const isCentered = placement === "center" || !anchorRef?.current;

    const progress = (currentStep != null && totalSteps != null)
        ? Math.round((currentStep / totalSteps) * 100)
        : null;

    const hasHole = !isCentered && (spotlightMode === "spotlight" || spotlightMode === "interactive");

    const arrowStyles = {
        bottom: "absolute -top-[16px] left-5 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[16px] border-b-base-text/20",
        top: "absolute -bottom-[16px] left-5 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[16px] border-t-base-text/20",
        right: "absolute -left-[16px] top-5 w-0 h-0 border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent border-r-[16px] border-r-base-text/20",
        left: "absolute -right-[16px] top-5 w-0 h-0 border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent border-l-[16px] border-l-base-text/20",
        "bottom-right": "absolute -top-[16px] right-5 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[16px] border-b-base-text/20",
        "bottom-left": "absolute -top-[16px] left-5 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[16px] border-b-base-text/20",
        "top-right": "absolute -bottom-[16px] right-5 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[16px] border-t-base-text/20",
        "top-left": "absolute -bottom-[16px] left-5 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[16px] border-t-base-text/20",
        "top-inside": "absolute -top-[16px] left-5 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[16px] border-b-base-text/20",
        "bottom-inside": "absolute -bottom-[16px] left-5 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[16px] border-t-base-text/20",
    }

    const arrowInnerStyles = {
        bottom: "absolute -top-[15px] left-[21px] w-0 h-0 border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent border-b-[15px] border-b-white/90 dark:border-b-base-inner",
        top: "absolute -bottom-[15px] left-[21px] w-0 h-0 border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent border-t-[15px] border-t-white/90 dark:border-t-base-inner",
        right: "absolute -left-[15px] top-[21px] w-0 h-0 border-t-[9px] border-t-transparent border-b-[9px] border-b-transparent border-r-[15px] border-r-white/90 dark:border-r-base-inner",
        left: "absolute -right-[15px] top-[21px] w-0 h-0 border-t-[9px] border-t-transparent border-b-[9px] border-b-transparent border-l-[15px] border-l-white/90 dark:border-l-base-inner",
        "bottom-right": "absolute -top-[15px] right-[21px] w-0 h-0 border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent border-b-[15px] border-b-white/90 dark:border-b-base-inner",
        "bottom-left": "absolute -top-[15px] left-[21px] w-0 h-0 border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent border-b-[15px] border-b-white/90 dark:border-b-base-inner",
        "top-right": "absolute -bottom-[15px] right-[21px] w-0 h-0 border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent border-t-[15px] border-t-white/90 dark:border-t-base-inner",
        "top-left": "absolute -bottom-[15px] left-[21px] w-0 h-0 border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent border-t-[15px] border-t-white/90 dark:border-t-base-inner",
        "top-inside": "absolute -top-[15px] left-[21px] w-0 h-0 border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent border-b-[15px] border-b-white/90 dark:border-b-base-inner",
        "bottom-inside": "absolute -bottom-[15px] left-[21px] w-0 h-0 border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent border-t-[15px] border-t-white/90 dark:border-t-base-inner",
    }

    // Keep anchorRect in sync with scroll/resize
    const updateAnchorRect = useCallback(() => {
        if (!anchorRef?.current) return;
        setAnchorRect(anchorRef.current.getBoundingClientRect());
    }, [anchorRef]);

    useEffect(() => {
        if (!visible || isCentered) return;
        window.addEventListener('scroll', updateAnchorRect, true);
        window.addEventListener('resize', updateAnchorRect);
        return () => {
            window.removeEventListener('scroll', updateAnchorRect, true);
            window.removeEventListener('resize', updateAnchorRect);
        };
    }, [visible, isCentered, updateAnchorRect]);

    useEffect(() => {
        // Centered mode: skip all anchor-rect/scrollIntoView logic, just fade in after delay
        if (isCentered) {
            const timeout = setTimeout(() => {
                setAnchorRect(null);
                setCoords({
                    top: `calc(50% + ${top - bottom}px)`,
                    left: `calc(50% + ${left - right}px)`,
                    transform: 'translate(-50%, -50%)',
                });
                setVisible(true);
            }, delay);
            return () => clearTimeout(timeout);
        }

        if (!anchorRef?.current) return;

        if (!disableScroll) {
            anchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        const timeout = setTimeout(() => {
            const rect = anchorRef.current?.getBoundingClientRect();
            if (!rect) return;

            // Skip if element is not visible in the DOM
            if (anchorRef.current.offsetParent === null && getComputedStyle(anchorRef.current).position !== 'fixed') return;

            setAnchorRect(rect);

            if (placement === "bottom") {
                setCoords({ top: rect.bottom + 16 + bottom, left: rect.left + left });
            } else if (placement === "top") {
                setCoords({ top: rect.top - 16 - top, left: rect.left + left, transform: 'translateY(-100%)' });
            } else if (placement === "right") {
                setCoords({ top: rect.top - top, left: rect.right + 16 + right });
            } else if (placement === "left") {
                setCoords({ top: rect.top + top, left: rect.left - 16, transform: 'translateX(-100%)' });
            } else if (placement === "bottom-right") {
                setCoords({ top: rect.bottom + 16 + bottom, left: rect.right + left, transform: 'translateX(-100%)' });
            } else if (placement === "bottom-left") {
                setCoords({ top: rect.bottom + 16 + bottom, left: rect.left + left });
            } else if (placement === "top-right") {
                setCoords({ top: rect.top - 16 - top, left: rect.right + left, transform: 'translate(-100%, -100%)' });
            } else if (placement === "top-left") {
                setCoords({ top: rect.top - 16 - top, left: rect.left + left, transform: 'translateY(-100%)' });
            } else if (placement === "top-inside") {
                setCoords({ top: rect.top + 12 + top, left: rect.left + left });
            } else if (placement === "bottom-inside") {
                setCoords({ top: rect.bottom - 12 - bottom, left: rect.left + left, transform: 'translateY(-100%)' });
            }

            if (useRoughUnderline && anchorRef.current) {
                const annotation = annotate(anchorRef.current, {
                    type: "box",
                    color: "#D2A57D",
                    animationDuration: 600,
                    padding: 2,
                });
                annotationRef.current = annotation;
                annotation.show();
            }

            setVisible(true);
        }, disableScroll ? delay : Math.max(delay, 600));

        return () => {
            clearTimeout(timeout);
            if (annotationRef.current) annotationRef.current.remove();
        };
    }, [anchorRef, placement, top, left, bottom, right, useRoughUnderline, disableScroll, delay, isCentered]);

    useEffect(() => {
        if (visible) setTimeout(() => setProgressVisible(true), 100);
        else setProgressVisible(false);
    }, [visible]);

    useEffect(() => {
        if (isCentered || !anchorRef?.current || !visible || !hasHole) return;
        const el = anchorRef.current;
        const container = containerRef?.current;

        const prevZIndex = el.style.zIndex;
        const prevPointerEvents = el.style.pointerEvents;
        const prevPosition = el.style.position;
        const computedPosition = getComputedStyle(el).position;

        if (computedPosition !== 'fixed') el.style.position = 'relative';
        el.style.zIndex = '10000002';
        el.style.pointerEvents = spotlightMode === "interactive" ? 'auto' : 'none';

        let prevContainerZ, prevContainerPosition;
        if (container) {
            prevContainerZ = container.style.zIndex;
            prevContainerPosition = container.style.position;
            const computedContainerPosition = getComputedStyle(container).position;
            if (computedContainerPosition !== 'fixed') container.style.position = 'relative';
            container.style.zIndex = '10000001';
        }

        return () => {
            if (computedPosition !== 'fixed') el.style.position = prevPosition;
            el.style.zIndex = prevZIndex;
            el.style.pointerEvents = prevPointerEvents;
            if (container) {
                container.style.zIndex = prevContainerZ;
                container.style.position = prevContainerPosition;
            }
        };
    }, [visible, anchorRef, containerRef, spotlightMode, hasHole, isCentered]);

    const resolvePadding = (p) => {
        if (typeof p === 'number') return { top: p, bottom: p, left: p, right: p };
        if ('x' in p || 'y' in p) return { top: p.y ?? 8, bottom: p.y ?? 8, left: p.x ?? 8, right: p.x ?? 8 };
        return { top: p.top ?? 8, bottom: p.bottom ?? 8, left: p.left ?? 8, right: p.right ?? 8 };
    };
    const pad = resolvePadding(spotlightPadding);

    const dimColor = 'rgba(0,0,0,0.35)';
    const blockStyle = { position: 'fixed', zIndex: 9999999, pointerEvents: 'all', background: dimColor, opacity: visible ? 1 : 0, transition: 'opacity 0.3s ease' };

    const getButtonContent = () => {
        switch (type) {
            case "onboardingPopover": {
                return <MoveRight className="text-base-text size-5" strokeWidth={1} />
            }
            case "nudge": {
                return <MoveRight className="text-base-text size-5" strokeWidth={1} />
            }
            case "hint": {
                return <MoveRight className="text-base-text size-5" strokeWidth={1} />
            }
        }
    }

    const renderOverlay = () => {
        // Centered mode: just a plain full-screen dim, no hole
        if (isCentered) {
            return (
                <div
                    style={{ ...blockStyle, inset: 0 }}
                    onClick={(e) => e.stopPropagation()}
                />
            );
        }

        // "none": single full-screen div, simple
        if (spotlightMode === "none") {
            return (
                <div
                    style={{ ...blockStyle, inset: 0 }}
                    onClick={(e) => e.stopPropagation()}
                />
            );
        }

        // "spotlight" or "interactive": SVG mask for the dim + hole visual,
        // but for "interactive" we use the 4-strip div approach so there is
        // literally zero DOM sitting over the anchor hole.
        if (spotlightMode === "interactive" && anchorRect) {
            const { top: aTop, left: aLeft, right: aRight, bottom: aBot } = anchorRect;
            return (
                <>
                    {/* Top strip */}
                    <div style={{ ...blockStyle, top: 0, left: 0, right: 0, height: Math.max(0, aTop - pad.top) }} onClick={(e) => e.stopPropagation()} />
                    {/* Bottom strip */}
                    <div style={{ ...blockStyle, top: aBot + pad.bottom, left: 0, right: 0, bottom: 0 }} onClick={(e) => e.stopPropagation()} />
                    {/* Left strip */}
                    <div style={{ ...blockStyle, top: aTop - pad.top, left: 0, width: Math.max(0, aLeft - pad.left), height: anchorRect.height + pad.top + pad.bottom }} onClick={(e) => e.stopPropagation()} />
                    {/* Right strip */}
                    <div style={{ ...blockStyle, top: aTop - pad.top, left: aRight + pad.right, right: 0, height: anchorRect.height + pad.top + pad.bottom }} onClick={(e) => e.stopPropagation()} />
                </>
            );
        }

        // "spotlight": SVG with mask — hole is visible but blocked
        if (spotlightMode === "spotlight" && anchorRect) {
            return (
                <svg
                    style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', zIndex: 9999999, pointerEvents: 'all', opacity: visible ? 1 : 0, transition: 'opacity 0.3s ease' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <defs>
                        <mask id="onboarding-spotlight-mask">
                            <rect width="100%" height="100%" fill="white" />
                            <rect x={anchorRect.left - pad.left} y={anchorRect.top - pad.top} width={anchorRect.width + pad.left + pad.right} height={anchorRect.height + pad.top + pad.bottom} rx="10" ry="10" fill="black" />
                        </mask>
                    </defs>
                    <rect width="100%" height="100%" fill={dimColor} mask="url(#onboarding-spotlight-mask)" />
                    <rect x={anchorRect.left - pad.left} y={anchorRect.top - pad.top} width={anchorRect.width + pad.left + pad.right} height={anchorRect.height + pad.top + pad.bottom} rx="10" ry="10" fill="transparent" style={{ pointerEvents: 'all' }} onClick={(e) => e.stopPropagation()} />
                    <rect x={anchorRect.left - pad.left} y={anchorRect.top - pad.top} width={anchorRect.width + pad.left + pad.right} height={anchorRect.height + pad.top + pad.bottom} rx="10" ry="10" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" style={{ pointerEvents: 'none' }} />
                </svg>
            );
        }

        return null;
    };

    return createPortal(
        <>
            {visible && renderOverlay()}

            {/* ── Popover ── */}
            <div
                onClick={(e) => e.stopPropagation()}
                className={`${className} fixed bg-white/90 dark:bg-zinc-900/70 shadow-sm border border-base-text/20 rounded-2xl pl-3 pr-4 py-4 flex flex-col gap-2`}
                style={{
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    zIndex: 10000003,
                    top: coords.top,
                    left: coords.left,
                    transform: coords.transform,
                    opacity: visible ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                    // pointerEvents: 'auto',
                    pointerEvents: visible ? 'auto' : 'none',
                }}
            >
                <div className={`${(hideArrow || isCentered) && "hidden"}`}>
                    <div className={arrowStyles[placement]} />
                    <div className={arrowInnerStyles[placement]} />
                </div>

                {(type === "nudge" || placement === "center") &&
                    <div className="text-base-text font-light text-tiny flex justify-center gap-2">
                        {startContent}
                    </div>
                }


                <div className="text-base-text font-light text-[13px] tracking-wide  flex gap-2">
                    {(type !== "nudge" && placement !== "center") && startContent}
                    {resolvedDescription}
                </div>

                <div className="w-full flex gap-2 items-center">
                    {(progress !== null && !hideProgress) && (
                        <div className="flex items-center gap-2 mt-1 flex-1">
                            <div className="flex-1 h-[5px] bg-base-text/10 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-button rounded-full transition-all duration-500 ease-out"
                                    style={{ width: progressVisible ? `${progress}%` : '0%' }}
                                />
                            </div>
                            <span className="text-base-text/50 font-light shrink-0" style={{ fontSize: '10px' }}>
                                {currentStep}/{totalSteps}
                            </span>
                        </div>
                    )}

                    {!hideButtons &&
                        <div className={`${(progress === null || hideProgress) && "w-full gap-2"} flex justify-end`}>

                            {allowSkip &&
                                <Button
                                    size="sm"
                                    className={`font-light text-base-text ${buttonContent && "bg-button/10"}`}
                                    onPress={async () => {
                                        if (isSkipPending) return
                                        sessionStorage.setItem('onboarding_active_session', 'true');
                                        setIsSkipPending(true)
                                        try {
                                            await onNext()
                                        } finally {
                                            setIsSkipPending(false)
                                        }
                                    }}
                                    isLoading={isSkipPending}
                                    variant="light"
                                >
                                    スキップ
                                </Button>
                            }
                            <Button
                                size="sm"
                                isIconOnly={!buttonContent}
                                isDisabled={isSendDisabled}
                                className={`font-light text-white ${buttonContent && "bg-button"}`}
                                onPress={async () => {
                                    if (isPending || isSendDisabled) return
                                    sessionStorage.setItem('onboarding_active_session', 'true');
                                    setIsPending(true)
                                    try {
                                        await onNext(inputValue)
                                    } finally {
                                        setIsPending(false)
                                    }
                                }}
                                isLoading={isPending}
                                variant="light"
                            >
                                {buttonContent ?? getButtonContent()}
                            </Button>
                        </div>
                    }
                </div>
            </div>
        </>,
        document.body
    );
}
// export const TutorialPopover = ({
//     className = "",
//     startContent = <BotIcon className="size-7 text-base-text shrink-0" />,
//     description = <></>,
//     onNext = () => { },
//     anchorRef,
//     placement = "bottom",

//     top = 0, right = 0, left = 0, bottom = 0,
//     useRoughUnderline = false,
//     hideArrow = false,
//     disableScroll = false,
//     delay = 600,

//     currentStep = null,
//     totalSteps = null,

//     spotlightPadding = 8,
//     // spotlightPadding = { top: 8, bottom: 8, left: 8, right: -200 },
//     spotlightMode = "spotlight",
//     containerRef = null
// }) => {
//     const isDesktop = useMediaQuery(process.env.REACT_APP_IS_DESKTOP);
//     const [coords, setCoords] = useState({ top: 0, left: 0 });
//     const [anchorRect, setAnchorRect] = useState(null);
//     const [visible, setVisible] = useState(false);
//     const [progressVisible, setProgressVisible] = useState(false);
//     const annotationRef = useRef(null);

//     const progress = (currentStep != null && totalSteps != null)
//         ? Math.round((currentStep / totalSteps) * 100)
//         : null;

//     const hasHole = spotlightMode === "spotlight" || spotlightMode === "interactive";

//     const arrowStyles = {
//         bottom: "absolute -top-[16px] left-5 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[16px] border-b-base-text/20",
//         top: "absolute -bottom-[16px] left-5 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[16px] border-t-base-text/20",
//         right: "absolute -left-[16px] top-5 w-0 h-0 border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent border-r-[16px] border-r-base-text/20",
//         left: "absolute -right-[16px] top-5 w-0 h-0 border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent border-l-[16px] border-l-base-text/20",
//         "bottom-right": "absolute -top-[16px] right-5 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[16px] border-b-base-text/20",
//         "bottom-left": "absolute -top-[16px] left-5 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[16px] border-b-base-text/20",
//         "top-right": "absolute -bottom-[16px] right-5 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[16px] border-t-base-text/20",
//         "top-left": "absolute -bottom-[16px] left-5 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[16px] border-t-base-text/20",
//         "top-inside": "absolute -top-[16px] left-5 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[16px] border-b-base-text/20",
//         "bottom-inside": "absolute -bottom-[16px] left-5 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[16px] border-t-base-text/20",
//     }

//     const arrowInnerStyles = {
//         bottom: "absolute -top-[15px] left-[21px] w-0 h-0 border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent border-b-[15px] border-b-white/90 dark:border-b-base-inner",
//         top: "absolute -bottom-[15px] left-[21px] w-0 h-0 border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent border-t-[15px] border-t-white/90 dark:border-t-base-inner",
//         right: "absolute -left-[15px] top-[21px] w-0 h-0 border-t-[9px] border-t-transparent border-b-[9px] border-b-transparent border-r-[15px] border-r-white/90 dark:border-r-base-inner",
//         left: "absolute -right-[15px] top-[21px] w-0 h-0 border-t-[9px] border-t-transparent border-b-[9px] border-b-transparent border-l-[15px] border-l-white/90 dark:border-l-base-inner",
//         "bottom-right": "absolute -top-[15px] right-[21px] w-0 h-0 border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent border-b-[15px] border-b-white/90 dark:border-b-base-inner",
//         "bottom-left": "absolute -top-[15px] left-[21px] w-0 h-0 border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent border-b-[15px] border-b-white/90 dark:border-b-base-inner",
//         "top-right": "absolute -bottom-[15px] right-[21px] w-0 h-0 border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent border-t-[15px] border-t-white/90 dark:border-t-base-inner",
//         "top-left": "absolute -bottom-[15px] left-[21px] w-0 h-0 border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent border-t-[15px] border-t-white/90 dark:border-t-base-inner",
//         "top-inside": "absolute -top-[15px] left-[21px] w-0 h-0 border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent border-b-[15px] border-b-white/90 dark:border-b-base-inner",
//         "bottom-inside": "absolute -bottom-[15px] left-[21px] w-0 h-0 border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent border-t-[15px] border-t-white/90 dark:border-t-base-inner",
//     }

//     // Keep anchorRect in sync with scroll/resize
//     const updateAnchorRect = useCallback(() => {
//         if (!anchorRef?.current) return;
//         setAnchorRect(anchorRef.current.getBoundingClientRect());
//     }, [anchorRef]);

//     useEffect(() => {
//         if (!visible) return;
//         window.addEventListener('scroll', updateAnchorRect, true);
//         window.addEventListener('resize', updateAnchorRect);
//         return () => {
//             window.removeEventListener('scroll', updateAnchorRect, true);
//             window.removeEventListener('resize', updateAnchorRect);
//         };
//     }, [visible, updateAnchorRect]);

//     useEffect(() => {
//         if (!anchorRef?.current) return;

//         if (!disableScroll) {
//             anchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
//         }

//         const timeout = setTimeout(() => {
//             // const rect = anchorRef.current?.getBoundingClientRect();
//             // if (!rect) return;

//             // setAnchorRect(rect);
//             const rect = anchorRef.current?.getBoundingClientRect();
//             if (!rect) return;

//             // Skip if element is not visible in the DOM
//             if (anchorRef.current.offsetParent === null && getComputedStyle(anchorRef.current).position !== 'fixed') return;

//             setAnchorRect(rect);

//             if (placement === "bottom") {
//                 setCoords({ top: rect.bottom + 16 + bottom, left: rect.left + left });
//             } else if (placement === "top") {
//                 setCoords({ top: rect.top - 16 - top, left: rect.left + left, transform: 'translateY(-100%)' });
//             } else if (placement === "right") {
//                 setCoords({ top: rect.top - top, left: rect.right + 16 + right });
//             } else if (placement === "left") {
//                 setCoords({ top: rect.top + top, left: rect.left - 16, transform: 'translateX(-100%)' });
//             } else if (placement === "bottom-right") {
//                 setCoords({ top: rect.bottom + 16 + bottom, left: rect.right + left, transform: 'translateX(-100%)' });
//             } else if (placement === "bottom-left") {
//                 setCoords({ top: rect.bottom + 16 + bottom, left: rect.left + left });
//             } else if (placement === "top-right") {
//                 setCoords({ top: rect.top - 16 - top, left: rect.right + left, transform: 'translate(-100%, -100%)' });
//             } else if (placement === "top-left") {
//                 setCoords({ top: rect.top - 16 - top, left: rect.left + left, transform: 'translateY(-100%)' });
//             } else if (placement === "top-inside") {
//                 setCoords({ top: rect.top + 12 + top, left: rect.left + left });
//             } else if (placement === "bottom-inside") {
//                 setCoords({ top: rect.bottom - 12 - bottom, left: rect.left + left, transform: 'translateY(-100%)' });
//             }

//             if (useRoughUnderline && anchorRef.current) {
//                 const annotation = annotate(anchorRef.current, {
//                     type: "box",
//                     color: "#D2A57D",
//                     animationDuration: 600,
//                     padding: 2,
//                 });
//                 annotationRef.current = annotation;
//                 annotation.show();
//             }

//             setVisible(true);
//         }, disableScroll ? delay : Math.max(delay, 600));

//         return () => {
//             clearTimeout(timeout);
//             if (annotationRef.current) annotationRef.current.remove();
//         };
//     }, [anchorRef, placement, top, left, bottom, useRoughUnderline, disableScroll, delay]);

//     useEffect(() => {
//         if (visible) setTimeout(() => setProgressVisible(true), 100);
//         else setProgressVisible(false);
//     }, [visible]);

//     useEffect(() => {
//         if (!anchorRef?.current || 
//             !visible || 
//             !hasHole
//         ) return;
//         const el = anchorRef.current;
//         const container = containerRef?.current;

//         const prevZIndex = el.style.zIndex;
//         const prevPointerEvents = el.style.pointerEvents;
//         const prevPosition = el.style.position;
//         const computedPosition = getComputedStyle(el).position;

//         if (computedPosition !== 'fixed') el.style.position = 'relative';
//         el.style.zIndex = '10000002';
//         el.style.pointerEvents = spotlightMode === "interactive" ? 'auto' : 'none';

//         let prevContainerZ, prevContainerPosition;
//         if (container) {
//             prevContainerZ = container.style.zIndex;
//             prevContainerPosition = container.style.position;
//             const computedContainerPosition = getComputedStyle(container).position;
//             if (computedContainerPosition !== 'fixed') container.style.position = 'relative';
//             container.style.zIndex = '10000001';
//         }

//         return () => {
//             if (computedPosition !== 'fixed') el.style.position = prevPosition;
//             el.style.zIndex = prevZIndex;
//             el.style.pointerEvents = prevPointerEvents;
//             if (container) {
//                 container.style.zIndex = prevContainerZ;
//                 container.style.position = prevContainerPosition;
//             }
//         };
//     }, [visible, anchorRef, containerRef, spotlightMode, hasHole]);

//     const resolvePadding = (p) => {
//         if (typeof p === 'number') return { top: p, bottom: p, left: p, right: p };
//         if ('x' in p || 'y' in p) return { top: p.y ?? 8, bottom: p.y ?? 8, left: p.x ?? 8, right: p.x ?? 8 };
//         return { top: p.top ?? 8, bottom: p.bottom ?? 8, left: p.left ?? 8, right: p.right ?? 8 };
//     };
//     const pad = resolvePadding(spotlightPadding);

//     const dimColor = 'rgba(0,0,0,0.35)';
//     const blockStyle = { position: 'fixed', zIndex: 9999999, pointerEvents: 'all', background: dimColor, opacity: visible ? 1 : 0, transition: 'opacity 0.3s ease' };

//     const renderOverlay = () => {
//         // "none": single full-screen div, simple
//         if (spotlightMode === "none") {
//             return (
//                 <div
//                     style={{ ...blockStyle, inset: 0 }}
//                     onClick={(e) => e.stopPropagation()}
//                 />
//             );
//         }

//         // "spotlight" or "interactive": SVG mask for the dim + hole visual,
//         // but for "interactive" we use the 4-strip div approach so there is
//         // literally zero DOM sitting over the anchor hole.
//         if (spotlightMode === "interactive" && anchorRect) {
//             const { top: aTop, left: aLeft, right: aRight, bottom: aBot } = anchorRect;
//             const W = window.innerWidth;
//             const H = window.innerHeight;
//             return (
//                 <>
//                     {/* Top strip */}
//                     <div style={{ ...blockStyle, top: 0, left: 0, right: 0, height: Math.max(0, aTop - pad.top) }} onClick={(e) => e.stopPropagation()} />
//                     {/* Bottom strip */}
//                     <div style={{ ...blockStyle, top: aBot + pad.bottom, left: 0, right: 0, bottom: 0 }} onClick={(e) => e.stopPropagation()} />
//                     {/* Left strip */}
//                     <div style={{ ...blockStyle, top: aTop - pad.top, left: 0, width: Math.max(0, aLeft - pad.left), height: anchorRect.height + pad.top + pad.bottom }} onClick={(e) => e.stopPropagation()} />
//                     {/* Right strip */}
//                     <div style={{ ...blockStyle, top: aTop - pad.top, left: aRight + pad.right, right: 0, height: anchorRect.height + pad.top + pad.bottom }} onClick={(e) => e.stopPropagation()} />
//                     {/* Glow ring */}
//                     {/* <div style={{
//                         position: 'fixed',
//                         zIndex: 9999999,
//                         pointerEvents: 'none',
//                         top: aTop - pad.top,
//                         left: aLeft - pad.left,
//                         width: anchorRect.width + pad.left + pad.right,
//                         height: anchorRect.height + pad.top + pad.bottom,
//                         borderRadius: 10,
//                         boxShadow: '0 0 0 1.5px rgba(255,255,255,0.15)',
//                         opacity: visible ? 1 : 0,
//                         transition: 'opacity 0.3s ease',
//                     }} /> */}
//                 </>
//             );
//         }

//         // "spotlight": SVG with mask — hole is visible but blocked
//         if (spotlightMode === "spotlight" && anchorRect) {
//             return (
//                 <svg
//                     style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', zIndex: 9999999, pointerEvents: 'all', opacity: visible ? 1 : 0, transition: 'opacity 0.3s ease' }}
//                     onClick={(e) => e.stopPropagation()}
//                 >
//                     <defs>
//                         <mask id="onboarding-spotlight-mask">
//                             <rect width="100%" height="100%" fill="white" />
//                             <rect x={anchorRect.left - pad.left} y={anchorRect.top - pad.top} width={anchorRect.width + pad.left + pad.right} height={anchorRect.height + pad.top + pad.bottom} rx="10" ry="10" fill="black" />
//                         </mask>
//                     </defs>
//                     {/* Dim with hole */}
//                     <rect width="100%" height="100%" fill={dimColor} mask="url(#onboarding-spotlight-mask)"/>
//                     {/* Block clicks over the hole */}
//                     <rect x={anchorRect.left - pad.left} y={anchorRect.top - pad.top} width={anchorRect.width + pad.left + pad.right} height={anchorRect.height + pad.top + pad.bottom} rx="10" ry="10" fill="transparent" style={{ pointerEvents: 'all' }} onClick={(e) => e.stopPropagation()} />
//                     {/* Glow ring */}
//                     <rect x={anchorRect.left - pad.left} y={anchorRect.top - pad.top} width={anchorRect.width + pad.left + pad.right} height={anchorRect.height + pad.top + pad.bottom} rx="10" ry="10" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" style={{ pointerEvents: 'none' }} />
//                 </svg>
//             );
//         }

//         return null;
//     };

//     return createPortal(
//         <>
//             {visible && renderOverlay()}

//             {/* ── Popover ── */}
//             <div
//                 onClick={(e) => e.stopPropagation()}
//                 className={`${className} fixed bg-white/90 dark:bg-zinc-900/70 shadow-sm border border-base-text/20 rounded-2xl p-3 flex flex-col gap-2`}
//                 style={{
//                     backdropFilter: 'blur(16px)',
//                     WebkitBackdropFilter: 'blur(16px)',
//                     zIndex: 10000003,
//                     top: coords.top,
//                     left: coords.left,
//                     transform: coords.transform,
//                     opacity: visible ? 1 : 0,
//                     transition: 'opacity 0.2s ease',
//                     pointerEvents: 'auto',
//                 }}
//             >
//                 <div className={`${hideArrow && "hidden"}`}>
//                     <div className={arrowStyles[placement]} />
//                     <div className={arrowInnerStyles[placement]} />
//                 </div>

//                 <div className="text-base-text font-light text-tiny flex gap-2">
//                     {startContent}
//                     {description}
//                 </div>

//                 <div className="w-full flex gap-2 items-center">
//                     {progress !== null && (
//                         <div className="flex items-center gap-2 mt-1 flex-1">
//                             <div className="flex-1 h-[5px] bg-base-text/10 rounded-full overflow-hidden">
//                                 <div
//                                     className="h-full bg-button rounded-full transition-all duration-500 ease-out"
//                                     style={{ width: progressVisible ? `${progress}%` : '0%' }}
//                                 />
//                             </div>
//                             <span className="text-base-text/50 font-light shrink-0" style={{ fontSize: '10px' }}>
//                                 {currentStep}/{totalSteps}
//                             </span>
//                         </div>
//                     )}
//                     <div className="flex justify-end">
//                         <Button size="sm" isIconOnly onPress={() => {
//                             sessionStorage.setItem('onboarding_active_session', 'true');
//                             onNext()
//                         }} variant="light">
//                             <MoveRight className="text-base-text size-5" strokeWidth={1} />
//                         </Button>
//                     </div>
//                 </div>
//             </div>
//         </>,
//         document.body
//     );
// }